<template>
  <div class="sticker-area">
    <div class="sticker-categories">
      <swiper
        :pagination="{ clickable: true }"
        :breakpoints="{
          1200: { slidesPerView: 4.7, spaceBetween: 5 },
          991: { slidesPerView: 4.7, spaceBetween: 5 },
          576: { slidesPerView: 10.5, spaceBetween: 5 },
          0: { slidesPerView: 8.5, spaceBetween: 5 },
        }"
        class="swiper-container"
        ref="swiper"
      >
        <swiper-slide
          v-for="(sticker, index) in stickersList"
          :key="index"
          class="category"
          @click="selectCategory(sticker.id)"
          :class="{ active: sticker.id === selectedStickerId }"
        >
          <div class="sticker-wrapper">
            <img :src="sticker.cover" alt="" />
            <!-- 貼圖未購買鎖頭遮罩 -->
            <div v-if="sticker.type === StickerType.PURCHASABLE" class="lock-overlay">
              <img src="@/assets/img/streaming/lock.svg" alt="Locked" class="lock-icon" />
            </div>
          </div>
        </swiper-slide>
        <swiper-slide> </swiper-slide>
      </swiper>
    </div>
    <div class="sticker-content">
      <div class="sticker" v-for="sticker in selectedStickerIndexes" :key="sticker.src" @click="onStickerClick(sticker)">
        <img :src="sticker.src" :alt="sticker.alt" class="sticker-image" />
      </div>
    </div>
  </div>
</template>

<script>
import { Swiper, SwiperSlide } from 'swiper/vue';
import { getECommerceInfo, getEmojiProductDetail } from '@/api/giftShopService';

const StickerType = {
  OWNED: 'OWNED', // 已擁有的貼圖
  PURCHASABLE: 'PURCHASABLE', // 可以購買的貼圖
};

export default {
  name: 'StickerArea',
  components: {
    Swiper,
    SwiperSlide,
  },
  props: {
    stickers: Array,
    default: () => [],
    productId: String,
  },
  data() {
    return {
      StickerType,
      stickersList: [],
      selectedStickerId: null,
      selectedStickerIndexes: [],
      selectedStickerIsPurchasable: false,
    };
  },
  computed: {},
  mounted() {
    this.generateStickersList();
  },
  methods: {
    async generateStickersList() {
      try {
        // 為了取得對應直播貼圖的價格 取得禮物商店的所有物件
        const langSetting = this.$i18n?.locale === 'zh_tw' ? 'zh-tw' : this.$i18n?.locale || 'zh-tw';
        const response = await getECommerceInfo(langSetting);
        const giftItemData = response.data.data;

        // 取得直播專屬貼圖
        const purchasableStickers = await this.loadLiveStickerItems(this.productId, giftItemData);
        const mergedStickers = [...this.stickers, ...purchasableStickers];

        // 如果貼圖列表沒有改變 就不更新貼圖資料 避免每次刷新都重置玩家操作
        const newIds = mergedStickers.map((s) => s.emojiId).sort();
        const oldIds = this.stickersList.map((s) => s.emojiId).sort();
        const isSame = newIds.length === oldIds.length && newIds.every((id, i) => id === oldIds[i]);
        if (isSame) return;

        this.stickersList = mergedStickers.map((sticker) => ({
          id: sticker.emojiId,
          emojiId: sticker.emojiId,
          cover: this.generateStickerPackIconUrl(sticker.id),
          type: sticker.type || StickerType.OWNED,
          sessionId: sticker.sessionId || '_',
          packId: sticker.packId || '_',
          stickerCount: sticker.stickerCount,
          price: sticker.price,
        }));

        this.selectedStickerId = this.stickersList[0].id;
        this.selectCategory(this.selectedStickerId);
      } catch (error) {
        console.error('載入貼圖商店失敗', error);
      }
    },
    async loadLiveStickerItems(productId, giftItemData) {
      try {
        // 取得直播專屬貼圖
        const res = await getEmojiProductDetail(productId);
        const items = res.data.data || [];
        const result = [];

        // 此直播未持有專屬貼圖
        if (items.length === 0) {
          return [];
        }

        // 比對直播專屬貼圖和禮物庫的資訊 追加價格和ID並回傳完整資料
        for (const item of items) {
          const alreadyOwned = this.stickers.some((s) => s.emojiId === item.emojiId);
          if (alreadyOwned) continue;

          let matchedItem = null;
          for (const category of giftItemData) {
            matchedItem = category.items.find((giftItem) => giftItem.packId === item.packId && giftItem.sessionId === item.sessionId);
            if (matchedItem) {
              break;
            }
          }
          if (matchedItem) {
            result.push({
              id: item.id,
              emojiId: item.emojiId,
              type: StickerType.PURCHASABLE,
              sessionId: item.sessionId,
              packId: item.packId,
              stickerCount: item.stickerCount,
              price: matchedItem?.price ?? null,
            });
          }
        }
        return result;
      } catch (error) {
        console.error('載入對應直播貼圖失敗', error);
        return [];
      }
    },
    selectCategory(stickerId) {
      const selectedSticker = this.stickersList.find((sticker) => sticker.emojiId === stickerId);

      // 如果點擊貼圖是可購買的 就觸發購買
      if (selectedSticker?.type === StickerType.PURCHASABLE) {
        // 要購買的貼圖 通知父元件處理購買流程
        this.$emit('trigger-purchase', {
          sessionId: selectedSticker?.sessionId,
          packId: selectedSticker?.packId,
          price: selectedSticker?.price,
        });
      } else {
        this.selectedStickerIsPurchasable = false;
      }

      this.selectedStickerId = stickerId;
      this.selectedStickerIsPurchasable = selectedSticker.type === StickerType.PURCHASABLE; // 記錄貼圖是否尚未購買 後續是否可以發送貼圖由這個bool判斷

      if (selectedSticker) {
        this.selectedStickerIndexes = Array.from({ length: selectedSticker.stickerCount }, (_, index) => ({
          src: this.generateStickerUrl(selectedSticker.emojiId, index),
          alt: `${selectedSticker.emojiId}_${index}`,
        }));
      } else {
        this.selectedStickerIndexes = [];
      }
    },
    generateStickerUrl(id, index) {
      return `${this.$cloudFront}/Image/Public/chat_stickers/${id}/${index}.png`;
    },
    generateStickerPackIconUrl(id) {
      return `${this.$cloudFront}/Image/Public/emoji/${id}.png`;
    },
    onStickerClick(sticker) {
      if (this.selectedStickerIsPurchasable) {
        return;
      }
      this.$emit('sticker-selected', sticker);
    },
  },
};
</script>

<style lang="scss" scoped>
.sticker-wrapper {
  position: relative;
}
.lock-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 2;
  border-radius: 8px;

  img.lock-icon {
    position: absolute;
    top: -15px;
    right: 18px;
    opacity: 1;
  }
}
</style>
